import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { Observable, catchError, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { createNoCacheOptions } from '../utils/http-headers.util';

export interface SoapNotes {
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
}

export interface TranscriptionContent {
  patientHistory: string;
  diagnosticFindings: string;
  currentMedication: string;
  plan: string[];
  patientEducation: string;
}

export interface MedicalTerm {
  term: string;
  definition: string;
}

export interface MedicalTermsContent {
  terms: MedicalTerm[];
}

export interface ICD10Code {
  code: string;
  description: string;
}

export interface MedicalTerm {
  term: string;
  definition: string;
}

export interface AIAnalysisContent {
  keyFindings: string[];
  treatmentPlanSummary: string[];
  suggestedICD10Codes: ICD10Code[];
  medicalTerms: MedicalTerm[];
}

interface SoapNotesResponse {
  soapNotes: SoapNotes;
}

interface TranscriptionResponse {
  transcription: TranscriptionContent;
}

interface MedicalTermsResponse {
  medicalTerms: MedicalTermsContent;
}

interface AIAnalysisResponse {
  aiAnalysis: AIAnalysisContent;
}

@Injectable({
  providedIn: 'root'
})
export class TranscriptionDetailService {
  private soapNotes = signal<SoapNotes>({
    subjective: '',
    objective: '',
    assessment: '',
    plan: ''
  });



  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  loadSoapNotes(): Observable<SoapNotesResponse> {
    // For now, return default empty SOAP notes since we don't have a backend endpoint
    // This will be implemented when the backend provides this functionality
    const defaultSoapNotes: SoapNotes = {
      subjective: '',
      objective: '',
      assessment: '',
      plan: ''
    };
    
    return new Observable(observer => {
      // Check if there are saved notes first
      const savedNotes = this.loadSavedSoapNotes();
      if (savedNotes) {
        this.soapNotes.set(savedNotes);
      } else {
        this.soapNotes.set(defaultSoapNotes);
      }
      observer.next({ soapNotes: defaultSoapNotes });
      observer.complete();
    });
  }

  getSoapNotes() {
    return this.soapNotes;
  }

  updateSoapNotes(updates: Partial<SoapNotes>) {
    this.soapNotes.update(current => {
      const updated = { ...current, ...updates };
      return updated;
    });
  }

  /** Clears in-memory SOAP so a prior visit is not shown while loading another transcription. */
  clearSoapNotes(): void {
    this.soapNotes.set({
      subjective: '',
      objective: '',
      assessment: '',
      plan: ''
    });
  }

  resetSoapNotes() {
    this.loadSoapNotes().subscribe();
  }

  saveSoapNotes(): Observable<SoapNotes> {
    const currentNotes = this.soapNotes();
    
    // In a real application, this would be a POST request to save to the server
    // For now, we'll simulate saving by storing in localStorage
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('savedSoapNotes', JSON.stringify(currentNotes));
    }
    
    // Return the saved notes as an observable
    return new Observable(observer => {
      observer.next(currentNotes);
      observer.complete();
    });
  }

  loadSavedSoapNotes(): SoapNotes | null {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem('savedSoapNotes');
      if (saved) {
        return JSON.parse(saved);
      }
    }
    return null;
  }

  loadTranscriptionContent(): Observable<TranscriptionContent> {
    // For now, return default empty content since we don't have a backend endpoint
    // This will be implemented when the backend provides this functionality
    const defaultContent: TranscriptionContent = {
      patientHistory: '',
      diagnosticFindings: '',
      currentMedication: '',
      plan: [],
      patientEducation: ''
    };
    
    return new Observable(observer => {
      observer.next(defaultContent);
      observer.complete();
    });
  }

  loadMedicalTerms(): Observable<MedicalTermsContent> {
    // For now, return empty medical terms since we don't have a backend endpoint
    // This will be implemented when the backend provides this functionality
    const defaultTerms: MedicalTermsContent = {
      terms: []
    };
    
    return new Observable(observer => {
      observer.next(defaultTerms);
      observer.complete();
    });
  }

  loadAIAnalysis(transcriptionId: string): Observable<AIAnalysisContent> {
    // Call the backend to get AI Analysis content
    return this.http.get<AIAnalysisContent>(`${environment.apiUrl}/medical-analysis/ai-analysis?transcriptionResultId=${transcriptionId}`,
      createNoCacheOptions()
    ).pipe(
      catchError(error => {
        console.error('Error loading AI Analysis for transcription:', transcriptionId, error);
        // Only return fallback content if there was an actual error (not 404)
        if (error.status === 404) {
          // 404 means AI analysis doesn't exist - this is a data issue, not an error
          const noDataContent: AIAnalysisContent = {
            keyFindings: ['AI Analysis not available for this transcription.'],
            treatmentPlanSummary: ['Please use the SOAP Notes section for treatment planning.'],
            suggestedICD10Codes: [],
            medicalTerms: []
          };
          return of(noDataContent);
        } else {
          // Other errors (500, network issues, etc.) - this is a real error
          throw error;
        }
      })
    );
  }


}
